import { Button, Card, Col, Container, ListGroup, ListGroupItem, Pagination, Row } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';

import { TrackboardItem } from './TrackboardItem';

import { faCaretDown, faEye, faRocket, faSpinner, faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { callFhirPatient, FhirPatientVisit } from "../../amplify/appsync/fetch-sync-fhir";
import { DOWNLOAD_ALL_FAVORITES, FAVORITES_FOR_USERNAME_AND_TYPE, FAVORITE_LISTS_FOR_USERNAME, Favorite, Note, callQuery } from "../../amplify/appsync/query";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/appsync/search-patients';
import { getRandomFirstName, getRandomLastName, isDemoUser } from "../../utils/demo";
import { FavoriteContextMenu } from "./FavoriteContextMenu";
import { FavoriteNewListModal } from "./FavoriteNewListModal";
import { FavoriteColumnSelectModal } from "./FavoriteColumnSelectModal";
import { getStorage } from "../../utils/storage";
import pLimit from "p-limit";

import { DateType } from "../filters/dateTypes";
import './Favorites.css';
import { FavoriteSelectListModal } from "./FavoriteSelectListModal";
import { FilterTimeSelector } from "./FilterTimeSelector";
import { getSqlDateStrFromDateStr } from "../../utils/time";
import { getDefaultColumns } from "./trackboardUtils";
import { useGlobalOptions } from "../common/GlobalOptionsContext";

const MAX_RESULTS_TO_DISPLAY = 100;

const DEFAULT_COLUMNS = [
    {
        name: "Name",
        id: "name",
        size: 4,
    },
    {
        name: "Bed",
        id: "bed",
        size: 2,
    },
    {
        name: "CSN",
        id: "csn",
        size: 3,
    },
    {
        name: "MRN",
        id: "mrn",
        size: 3,
    },
    {
        name: "Age",
        id: "age",
        size: 1,
    },
    {
        name: "Sex",
        id: "gender",
        size: 1,
    },
    {
        name: "Acuity",
        id: "triage_acuity",
        size: 3,
    },
    {
        name: "CC",
        id: "chief_complaint",
        size: 4,
    },
    {
        name: "Dispo",
        id: "ed_dispo",
        size: 3,
    },
    {
        name: "Admit Risk",
        id: "admit_score",
        size: 2,
    },
    {
        name: "Revisit Risk",
        id: "epic_revisit_score",
        size: 2,
    },
    {
        name: "AFib",
        id: "afib_present",
        size: 2,
    },
    {
        name: "No Prev Arrhythmia/Anti-Coag",
        id: "afib_no_prev",
        size: 2,
    },
    {
        name: "CHA2DS2–VASc",
        id: "afib_cha",
        size: 2,
    },
    {
        name: "Elapsed",
        id: "elapsed",
        size: 2,
    },
    {
        name: "HR",
        id: "triage_hr",
        size: 1,
    },
    {
        name: "RR",
        id: "triage_rr",
        size: 1,
    },
    {
        name: "Temp",
        id: "triage_temp",
        size: 1,
    },
    {
        name: "SpO2",
        id: "triage_spo2",
        size: 1,
    },
    {
        name: "NBP",
        id: "triage_nbp",
        size: 1,
    },
    {
        name: "",
        id: "action",
        size: 2,
    },
];

export interface FavoriteMetadata {
    full_count: number | null,
}

export const Favorites = (props: AppAuthStateProps) => {
    const { getDerivedRole, getTrackboardColumns } = useGlobalOptions();

    const [isLoading, setIsLoading] = useState(false)
    const [allPatients, setAllPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata & FavoriteMetadata)[]>([]);
    const [activePatients, setActivePatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata & FavoriteMetadata)[]>([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [csnToReviewed, setCsnToReviewed] = useState<any>({});
    const [activePageNumber, setActivePageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalNumberOfResults, setTotalNumberOfResults] = useState<number>(0);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({x: 0, y: 0, patient: null, listName: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [showSelectList, setShowSelectList] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);
    const [filterSelectedFavoriteType, setFilterSelectedFavoriteType]: any = useState<any>("favorite");
    const [filterShowDateOptionsArrived, setFilterShowDateOptionsArrived] = useState<boolean>(false);
    const [filterShowDateOptionsAdded, setFilterShowDateOptionsAdded] = useState<boolean>(false);

    const [filterDateOptionsArrived, setFilterDateOptionsArrived]: any = useState<any>({
        dateOptionType: DateType.Absolute,
        relativeStart: null,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        // absoluteStart: new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 365),
        absoluteStart: null,
        absoluteEnd: new Date(),
    });
    const [filterDateOptionsAdded, setFilterDateOptionsAdded]: any = useState<any>({
        dateOptionType: DateType.Absolute,
        relativeStart: null,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        // absoluteStart: new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 365),
        absoluteStart: null,
        absoluteEnd: new Date(),
    });
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    // @ts-ignore
    const [columns, setColumns] = useState<any[]>(getDefaultColumns(getDerivedRole(props.user), getTrackboardColumns()));

    useEffect(() => {
        callQuery({
            operation: FAVORITE_LISTS_FOR_USERNAME,
        })
        .then((lists: any) => {
            lists = [
                "favorite",
                ...lists.map((list: any) => list.list_name)
            ];
            setFavoriteLists(lists);
        });
    }, []);

    useEffect(() => {
        refresh();
    }, [activePageNumber, filterSelectedFavoriteType, filterDateOptionsAdded, filterDateOptionsArrived]);

    useEffect(() => {
        setAllPatients([
            ...allPatients.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata & FavoriteMetadata)) => {
                return {
                    ...patient,
                    given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : "",
                    family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : "",
                }
            })),
        ])
        setActivePatients([
            ...allPatients.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata & FavoriteMetadata)) => {
                return {
                    ...patient,
                    given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : "",
                    family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : "",
                }
            })),
        ])
    }, [mrnToName]);

    const refresh = () => {
        setIsLoading(true);

        const filters = {
            operation: FAVORITES_FOR_USERNAME_AND_TYPE,
            limit: MAX_RESULTS_TO_DISPLAY,
            offset: (activePageNumber - 1) * MAX_RESULTS_TO_DISPLAY,
            favorite_type: filterSelectedFavoriteType,
            filters: {
                arrival_start: filterDateOptionsArrived.absoluteStart ? getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteStart) : null,
                arrival_end: filterDateOptionsArrived.absoluteEnd ? getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteEnd) : null,
                filter_time_start: filterDateOptionsAdded.absoluteStart ? getSqlDateStrFromDateStr(filterDateOptionsAdded.absoluteStart) : null,
                filter_time_end: filterDateOptionsAdded.absoluteEnd ? getSqlDateStrFromDateStr(filterDateOptionsAdded.absoluteEnd) : null,
            },
        };

        callQuery(filters)
        .then((patients: any[]) => {
            setIsLoading(false);
            const csnToFavs: any = {};
            for (let pt of patients) {
                if (!(pt.csn in csnToFavs)) {
                    csnToFavs[pt.csn] = [];
                }
                csnToFavs[pt.csn].push(pt);
            }
            setCsnToFavorites(csnToFavs);
            if (patients && patients.length > 0) {
                setTotalNumberOfResults(patients[0].full_count);
                const totalPages = Math.ceil(patients[0].full_count / MAX_RESULTS_TO_DISPLAY);
                setTotalPages(totalPages);
            } else {
                setTotalPages(0);
                setTotalNumberOfResults(0);
            }

            setAllPatients(patients);
            setActivePatients(patients);

            if (!isDemoUser(props.user)) {
                loadPatientNames(patients).then(() => {});
            } else {
                const mrnToName: any = {};
                for (let patient of patients) {
                    mrnToName[patient.mrn] = {
                        given: getRandomFirstName(),
                        family: getRandomLastName(),
                    }
                }
                setMrnToName(mrnToName);
            }
        });
    }

    const loadPatientNames = async (patients: PatientVisit[]) => {
        const limit = pLimit(2);
        const localMrnToName: any = {
            ...mrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            if (!(mrn in localMrnToName)) {
                promises.push(limit(() => {
                    return new Promise((resolve: any, reject: any) => {
                        callFhirPatient({
                            mrn: patient.mrn,
                        }).then((pt: FhirPatientVisit) => {
                            localMrnToName[patient.mrn] = pt;
                            // Update the names slowly, to avoid hogging the bandwidth
                            // which will prevent users from clicking on the patients
                            if (Object.keys(localMrnToName).length % 10 === 0) {
                                setMrnToName({
                                    ...localMrnToName
                                });
                            }
                            resolve(pt)
                        }).catch((err: any) => {
                            reject({});
                        })
                    })
                }))
            }
        }

        const result = await Promise.all(promises);
        for (let fhirPatientInfo of result) {
            localMrnToName[fhirPatientInfo.mrn] = fhirPatientInfo;
        }
        setMrnToName(localMrnToName);
    }

    const range = (start: number, stop: number, step: number) => {
        if (typeof stop == 'undefined') {
            // one param defined
            stop = start;
            start = 0;
        }
    
        if (typeof step == 'undefined') {
            step = 1;
        }
    
        if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
            return [];
        }
    
        var result = [];
        for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
            result.push(i);
        }
    
        return result;
    };

    const pageUpdate = (nextPageInput: number) => {
        let nextPage = nextPageInput;
        nextPage = Math.min(totalPages, nextPage);
        nextPage = Math.max(1, nextPage);
        setActivePageNumber(nextPage);
    };

    const download = () => {
        setIsDownloading(true);

        const filters = {
            operation: FAVORITES_FOR_USERNAME_AND_TYPE,
            limit: MAX_RESULTS_TO_DISPLAY,
            offset: (activePageNumber - 1) * MAX_RESULTS_TO_DISPLAY,
            favorite_type: filterSelectedFavoriteType,
            filters: {
                arrival_start: getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteStart),
                arrival_end: getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteEnd),
                filter_time_start: getSqlDateStrFromDateStr(filterDateOptionsAdded.absoluteStart),
                filter_time_end: getSqlDateStrFromDateStr(filterDateOptionsAdded.absoluteEnd),
            },
        };
        callQuery({
            operation: DOWNLOAD_ALL_FAVORITES,
            favorite_type: filterSelectedFavoriteType,
            filters: filters,
        })
        .then((obj: any) => {
            console.log("DOWNLOAD_ALL_FAVORITES", obj)
            setIsDownloading(false);
            window.open(obj.url, '_blank');
        });
    };

    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        const map: any = {...csnToFavorites};
        let newActivePatients = [...activePatients];
        if (!(csn in map)) {
            map[csn] = [];
        }
        if (favorite) {
            // newly saved favorite
            map[csn] = [];
            map[csn].push(favorite);
        } else {
            // favorite deleted
            map[csn] = [];
            if (map[csn].length === 0) {
                newActivePatients = newActivePatients.filter((pt: any) => pt.csn !== csn);
            }
        }
        setCsnToFavorites(map);
        setActivePatients(newActivePatients);
    }

    const updatePatientWithNote = (note: Note) => {
        setActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} onClick={handleClear} fluid>

                <Row style={{marginBottom: '12px'}}>
                    <Col md={9}>
                        <div style={{display: 'inline-block', position: 'relative'}}>
                            {/* <Button variant="secondary" size="sm" onClick={() => setFilterMapHighlightShow(true)}>
                                {HIGHLIGHT_MAP_OPTIONS[filterOptions["mapHighlight"]].name} <FontAwesomeIcon icon={faCaretDown} />
                            </Button> */}
                            <div className="favorites-selection-btn" onClick={() => {setShowSelectList(true)}}>
                                <strong><FontAwesomeIcon icon={faStar} /> {filterSelectedFavoriteType === "favorite" ? "Favorite" : filterSelectedFavoriteType}</strong>{' '}({totalNumberOfResults}){' '}<FontAwesomeIcon icon={faCaretDown} />
                            </div>
                            <FilterTimeSelector
                                filterDateOptions={filterDateOptionsArrived}
                                setFilterDateOptions={setFilterDateOptionsArrived}
                                filterDateSelectorShow={filterShowDateOptionsArrived}
                                setFilterDateSelectorShow={setFilterShowDateOptionsArrived}
                                placeholderText={"Arrived At"}
                            />{' '}
                            <FilterTimeSelector
                                filterDateOptions={filterDateOptionsAdded}
                                setFilterDateOptions={setFilterDateOptionsAdded}
                                filterDateSelectorShow={filterShowDateOptionsAdded}
                                setFilterDateSelectorShow={setFilterShowDateOptionsAdded}
                                placeholderText={"Added At"}
                            />
                        </div>
                    </Col>
                    <Col md={3} className="text-right">
                        <Button size="sm" onClick={download}>
                            {
                                isDownloading ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    <>Download All</>
                                )
                            }
                        </Button>
                    </Col>
                </Row>
                <Row style={{marginBottom: '24px'}}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div style={{display: 'block', width: '100%'}} >
                                        {
                                            columns.map((col) => {
                                                const totalRelativeCols = columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);
                                                if (col.id === "action") {
                                                    return (
                                                        <div key={col.id} className="text-right" style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>
                                                            <Button
                                                                variant="primary"
                                                                className={"trackboard-show-btn"}
                                                                onClick={() => setShowColumnSelect(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </Button>
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>{col.name}</div>
                                                    )
                                                }
                                            })
                                        }
                                    </div>
                                </ListGroupItem>
                                {
                                    isLoading ? (
                                        <>
                                            {
                                                [1, 2, 3].map((v) => (
                                                    <TrackboardItem 
                                                        key={v}
                                                        /** @ts-ignore */
                                                        patient={null}
                                                        columns={columns} 
                                                        csnToFavorites={csnToFavorites}
                                                        csnToReviewed={csnToReviewed}
                                                        pastFavorites={[]}
                                                        pastReviewed={[]}
                                                        setCsnToReviewed={setCsnToReviewed}
                                                        setShowContextMenu={setShowContextMenu}
                                                        setContextMenu={setContextMenu}
                                                        updatePatientWithNote={updatePatientWithNote}
                                                        padNotes={true}
                                                        isPlaceholder={true}
                                                        tenant={props.tenant}
                                                        user={props.user}
                                                    />
                                                ))
                                            }
                                        </>
                                    ) : (
                                        <>{activePatients.map((pt) => (
                                            <TrackboardItem 
                                                key={`ti-${pt.csn}`}
                                                patient={pt}
                                                columns={columns} 
                                                csnToFavorites={csnToFavorites}
                                                csnToReviewed={csnToReviewed}
                                                pastFavorites={[]}
                                                pastReviewed={[]}
                                                setCsnToReviewed={setCsnToReviewed}
                                                setShowContextMenu={setShowContextMenu}
                                                setContextMenu={setContextMenu}
                                                updatePatientWithNote={updatePatientWithNote}
                                                tenant={props.tenant}
                                                user={props.user}
                                            />
                                        ))}</>
                                    )
                                }
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                {
                    !isLoading && activePatients.length === 0 && (
                        <Container fluid style={{paddingTop: '26px', textAlign: 'center'}}>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '64px'}}><FontAwesomeIcon icon={faRocket} /></h1>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '15px'}}>No patient visits found matching criteria</h1>
                                </Col>
                            </Row>
                        </Container>
                    )
                }
                <FavoriteContextMenu show={showContextMenu} contextMenu={contextMenu} lists={favoriteLists} setShow={setShowContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={contextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteSelectListModal show={showSelectList} setShow={setShowSelectList} listNames={favoriteLists} activeList={filterSelectedFavoriteType} setSelectedList={setFilterSelectedFavoriteType} setListNames={setFavoriteLists}  />
                <FavoriteColumnSelectModal show={showColumnSelect} setShow={setShowColumnSelect} columns={columns} setColumns={setColumns} allColumns={getDefaultColumns(getDerivedRole(props.user))} saveKey="favorite_columns" />

                <Row>
                    <Col>  
                        <Pagination>
                            <Pagination.First onClick={() => setActivePageNumber(1)} />
                            <Pagination.Prev onClick={() => setActivePageNumber(Math.max(1, activePageNumber - 1))} />

                            {
                                totalPages < 6 ? (
                                    <>
                                        {
                                            range(1, totalPages + 1, 1).map((idx: number) => {
                                                if (idx === activePageNumber)
                                                    return <Pagination.Item key={idx} active onClick={() => pageUpdate(idx)}>{idx}</Pagination.Item>
                                                else
                                                    return <Pagination.Item key={idx} onClick={() => pageUpdate(idx)}>{idx}</Pagination.Item>
                                            })
                                        }
                                    </>
                                ) : (
                                    <>
                                        {
                                            activePageNumber - 4 > 1 && (
                                                <Pagination.Ellipsis />
                                            )
                                        }
                                        {
                                            range(Math.max(1, activePageNumber - 4), Math.min(totalPages, activePageNumber + 5), 1).map((idx: number) => {
                                                if (idx === activePageNumber) 
                                                    return <Pagination.Item key={idx} active onClick={() => pageUpdate(idx)}>{idx}</Pagination.Item>
                                                else
                                                    return <Pagination.Item key={idx} onClick={() => pageUpdate(idx)}>{idx}</Pagination.Item>
                                            })
                                        }
                                        {
                                            activePageNumber + 5 < totalPages + 1 && (
                                                <Pagination.Ellipsis />
                                            )
                                        }
                                    </>

                                )
                            }
                            <Pagination.Next onClick={() => pageUpdate(Math.min(totalPages, activePageNumber + 1))} />
                            <Pagination.Last onClick={() => pageUpdate(Math.max(totalPages - 1, 1))} />
                        </Pagination>
                    </Col>
                </Row>
            </Container>
        </>
    );
};
